import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

export interface SeoConfig {
  title: string;
  description: string;
  keywords: string;
  /** Canonical URL — full URL e.g. https://echoza.com/privacy-and-policy */
  canonicalUrl?: string;
  /** OG image absolute URL */
  ogImage?: string;
  /** 'en' | 'ar' */
  lang?: string;
  /** noindex pages (e.g. 404) */
  noIndex?: boolean;
}

/**
 * SeoService
 *
 * SSR-safe service that injects/updates <title>, <meta> and <link rel="canonical">
 * into the server-rendered HTML during prerendering, and updates them reactively
 * on the client when the language changes.
 *
 * Usage (in component constructor or resolve function):
 *   inject(SeoService).updateSeo({ title: '...', description: '...', ... });
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly _meta = inject(Meta);
  private readonly _title = inject(Title);
  private readonly _document = inject(DOCUMENT);

  updateSeo(config: SeoConfig): void {
    const {
      title,
      description,
      keywords,
      canonicalUrl,
      ogImage = 'https://echoza.com/imgs/og-image.png',
      lang = 'en',
      noIndex = false,
    } = config;

    const robots = noIndex ? 'noindex, nofollow' : 'index, follow';
    const ogLocale = lang === 'ar' ? 'ar_SA' : 'en_US';
    const url = canonicalUrl ?? 'https://echoza.com/';

    // ── 1. Title ──────────────────────────────────────────────────────────
    this._title.setTitle(title);

    // ── 2. Standard name-based meta tags ──────────────────────────────────
    this._upsertNameMeta('description', description);
    this._upsertNameMeta('keywords', keywords);
    this._upsertNameMeta('robots', robots);

    // ── 3. Open Graph (property-based) ────────────────────────────────────
    this._upsertPropertyMeta('og:type', 'website');
    this._upsertPropertyMeta('og:site_name', 'Echoza');
    this._upsertPropertyMeta('og:title', title);
    this._upsertPropertyMeta('og:description', description);
    this._upsertPropertyMeta('og:url', url);
    this._upsertPropertyMeta('og:image', ogImage);
    this._upsertPropertyMeta('og:image:width', '1200');
    this._upsertPropertyMeta('og:image:height', '630');
    this._upsertPropertyMeta('og:image:alt', 'Echoza – Social Media Management Platform');
    this._upsertPropertyMeta('og:locale', ogLocale);

    // ── 4. Twitter Card ───────────────────────────────────────────────────
    this._upsertNameMeta('twitter:card', 'summary_large_image');
    this._upsertNameMeta('twitter:site', '@echoza');
    this._upsertNameMeta('twitter:title', title);
    this._upsertNameMeta('twitter:description', description);
    this._upsertNameMeta('twitter:image', ogImage);

    // ── 5. Canonical link ─────────────────────────────────────────────────
    this._upsertCanonical(url);

    // ── 6. HTML lang/dir attributes ───────────────────────────────────────
    this._document.documentElement.setAttribute('lang', lang);
    this._document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Predefined route configs for use in route resolvers (SSR prerender)
  // ─────────────────────────────────────────────────────────────────────────

  static readonly HOME_EN: SeoConfig = {
    title: 'Echoza | Social Media Management Platform',
    description:
      'Everything you need to manage your digital presence in one place. Smart analytics, seamless collaboration, and a futuristic interface.',
    keywords: 'Social Media, Audience, Ads, AI, Analytics, Content, Marketing, CRM, Dashboard, Campaign',
    canonicalUrl: 'https://echoza.com/',
    lang: 'en',
  };

  static readonly PRIVACY_EN: SeoConfig = {
    title: 'Privacy Policy | Echoza',
    description:
      'Learn how Echoza collects, uses, and protects your personal data on our social media management platform.',
    keywords: 'privacy policy, Echoza, data protection, GDPR, user data',
    canonicalUrl: 'https://echoza.com/privacy-and-policy',
    lang: 'en',
  };

  static readonly TERMS_EN: SeoConfig = {
    title: 'Terms & Conditions | Echoza',
    description:
      'Read the terms and conditions governing your use of Echoza, the social media management platform.',
    keywords: 'terms and conditions, Echoza, user agreement, usage policy',
    canonicalUrl: 'https://echoza.com/terms-and-conditions',
    lang: 'en',
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Private helpers
  // ─────────────────────────────────────────────────────────────────────────

  private _upsertNameMeta(name: string, content: string): void {
    if (!this._meta.getTag(`name="${name}"`)) {
      this._meta.addTag({ name, content });
    } else {
      this._meta.updateTag({ name, content });
    }
  }

  private _upsertPropertyMeta(property: string, content: string): void {
    const existing = this._meta.getTag(`property="${property}"`);
    if (existing) {
      this._meta.removeTagElement(existing);
    }
    this._meta.addTag({ property, content });
  }

  private _upsertCanonical(url: string): void {
    const head = this._document.head;
    let link: HTMLLinkElement | null = head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this._document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
